// Configuracao global de encaminhamento por tenant (padrao global) e override
// por canal de origem. Tipos + defaults + normalizacao de linha crua do banco.
//
// Funcoes puras: sem I/O. Consumidas pelo bot (aplicacao real) e pelo painel
// (edicao + simulador), garantindo que os dois lados usem a MESMA regra.
import { DEFAULT_WINDOW, normalizeWindow } from "./window.js";
import { normalizeDomainList } from "./domains.js";
import { normalizeTermList } from "./terms.js";
export const MAX_IGNORED_TERMS = 50;
export const MAX_CUSTOM_SHORTENERS = 50;
export const MAX_USER_TEMPLATES = 10;
export const DEFAULT_DEDUP_WINDOW_MINUTES = 360;
export const MAX_DEDUP_WINDOW_MINUTES = 7 * 24 * 60;
export const DEFAULT_VALUE_FILTER = Object.freeze({
    enabled: false,
    minPriceCents: null,
    maxPriceCents: null,
    minDiscountPercent: null,
    priceUnknownAction: "pass",
});
export const DEFAULT_FORWARDING_SETTINGS = Object.freeze({
    mode: "simple",
    window: DEFAULT_WINDOW,
    useTemplate: false,
    linkStyle: "short",
    ignoredTerms: [],
    customShorteners: [],
    valueFilter: DEFAULT_VALUE_FILTER,
    dedupWindowMinutes: DEFAULT_DEDUP_WINDOW_MINUTES,
    defaultTemplateRef: null,
    templateByProvider: {},
});
function asRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function asCents(value) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        return null;
    }
    return Math.round(value);
}
function asPercent(value) {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return Math.min(100, value);
}
function asStringArray(value) {
    return Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];
}
/** Normaliza um value filter cru (jsonb snake_case ou camelCase). */
export function normalizeValueFilter(raw) {
    const r = asRecord(raw);
    return {
        enabled: r["enabled"] === true,
        minPriceCents: asCents(r["min_price_cents"] ?? r["minPriceCents"]),
        maxPriceCents: asCents(r["max_price_cents"] ?? r["maxPriceCents"]),
        minDiscountPercent: asPercent(r["min_discount_percent"] ?? r["minDiscountPercent"]),
        priceUnknownAction: (r["price_unknown_action"] ?? r["priceUnknownAction"]) === "drop" ? "drop" : "pass",
    };
}
function normalizeTemplateMap(raw) {
    const out = {};
    for (const [key, value] of Object.entries(asRecord(raw))) {
        if (typeof value === "string" && value.length > 0) {
            out[key] = value;
        }
    }
    return out;
}
function clampDedupWindow(value) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        return DEFAULT_DEDUP_WINDOW_MINUTES;
    }
    return Math.min(MAX_DEDUP_WINDOW_MINUTES, Math.round(value));
}
/** Converte a linha crua de forwarding_settings em ForwardingSettings valido.
 *  Linha ausente (null) = defaults. Valores invalidos caem no default seguro. */
export function normalizeSettings(raw) {
    if (raw === null || raw === undefined) {
        return DEFAULT_FORWARDING_SETTINGS;
    }
    const r = asRecord(raw);
    const templateRef = r["default_template_ref"];
    return {
        mode: r["mode"] === "advanced" ? "advanced" : "simple",
        window: normalizeWindow(r["window"]),
        useTemplate: r["use_template"] === true,
        linkStyle: r["link_style"] === "direct" ? "direct" : "short",
        ignoredTerms: normalizeTermList(asStringArray(r["ignored_terms"])).slice(0, MAX_IGNORED_TERMS),
        customShorteners: normalizeDomainList(asStringArray(r["custom_shorteners"])).slice(0, MAX_CUSTOM_SHORTENERS),
        valueFilter: normalizeValueFilter(r["value_filter"]),
        dedupWindowMinutes: clampDedupWindow(r["dedup_window_minutes"]),
        defaultTemplateRef: typeof templateRef === "string" && templateRef.length > 0 ? templateRef : null,
        templateByProvider: normalizeTemplateMap(r["template_by_provider"]),
    };
}
/** Converte a linha crua de forwarding_channel_overrides. */
export function normalizeOverride(raw) {
    const r = asRecord(raw);
    const platform = r["platform"];
    const channelRef = r["channel_ref"];
    if ((platform !== "telegram" && platform !== "whatsapp") || typeof channelRef !== "string") {
        return null;
    }
    const providers = r["allowed_providers"];
    const linkStyle = r["link_style"];
    return {
        platform,
        channelRef,
        allowedProviders: Array.isArray(providers) ? asStringArray(providers) : null,
        ignoredTerms: normalizeTermList(asStringArray(r["ignored_terms"])).slice(0, MAX_IGNORED_TERMS),
        linkStyle: linkStyle === "short" || linkStyle === "direct" ? linkStyle : null,
        valueFilter: r["value_filter"] ? normalizeValueFilter(r["value_filter"]) : null,
    };
}
export function resolveEffectiveConfig(settings, override) {
    if (!override) {
        return {
            settings,
            allowedProviders: null,
            ignoredTerms: settings.ignoredTerms,
            linkStyle: settings.linkStyle,
            valueFilter: settings.valueFilter,
        };
    }
    return {
        settings,
        allowedProviders: override.allowedProviders,
        ignoredTerms: normalizeTermList([...settings.ignoredTerms, ...override.ignoredTerms]),
        linkStyle: override.linkStyle ?? settings.linkStyle,
        valueFilter: override.valueFilter ?? settings.valueFilter,
    };
}
/** Providers deeplink (Amazon, ML, Magalu) NUNCA encurtam, por ToS. */
export const NEVER_SHORTEN_PROVIDERS = new Set([
    "amazon",
    "mercadolivre",
    "magalu",
]);
export function shouldShorten(linkStyle, providerCode) {
    return linkStyle === "short" && !NEVER_SHORTEN_PROVIDERS.has(providerCode);
}
//# sourceMappingURL=settings.js.map